import { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import type { RadarBubble } from '@/services/api/teacher'

interface UseRadarFocusOptions {
  bubbles: RadarBubble[]
  onBubbleSelect?: (bubble: RadarBubble | null) => void
  onBubbleActivate?: (bubble: RadarBubble) => void
}

interface UseRadarFocusReturn {
  /** Currently focused bubble ID */
  focusedId: string | null
  /** Set focused bubble by ID */
  setFocusedId: (id: string | null) => void
  /** Get the currently focused bubble */
  focusedBubble: RadarBubble | null
  /** Handle keyboard events */
  handleKeyDown: (e: KeyboardEvent) => void
  /** Move focus to next bubble */
  focusNext: () => void
  /** Move focus to previous bubble */
  focusPrevious: () => void
  /** Clear focus */
  clearFocus: () => void
}

/**
 * Hook for keyboard navigation in the radar
 * Provides Tab, Arrow keys, Enter, and Escape handling
 *
 * Phase 2.1: Keyboard Navigation
 *
 * Navigation Logic:
 * - Tab: Next bubble (cycles through)
 * - Shift+Tab: Previous bubble
 * - ArrowRight: Next bubble by angle
 * - ArrowLeft: Previous bubble by angle
 * - ArrowUp: Bubble closer to center (more urgent)
 * - ArrowDown: Bubble farther from center (less urgent)
 * - Enter/Space: Activate bubble
 * - Escape: Clear focus
 */
export function useRadarFocus({
  bubbles,
  onBubbleSelect,
  onBubbleActivate,
}: UseRadarFocusOptions): UseRadarFocusReturn {
  const [focusedId, setFocusedId] = useState<string | null>(null)
  const containerRef = useRef<HTMLElement | null>(null)

  // Sort bubbles for logical navigation order
  // Priority: Panic zone first, then by angle
  const sortedBubbles = useMemo(() => {
    return [...bubbles].sort((a, b) => {
      // Panic zone bubbles first
      const aPanic = a.is_in_panic_zone ? 0 : a.is_in_middle_zone ? 1 : 2
      const bPanic = b.is_in_panic_zone ? 0 : b.is_in_middle_zone ? 1 : 2
      if (aPanic !== bPanic) return aPanic - bPanic
      // Then by angle
      return a.angle_degrees - b.angle_degrees
    })
  }, [bubbles])

  // Get focused bubble
  const focusedBubble = useMemo(() => {
    return bubbles.find(b => b.id === focusedId) || null
  }, [bubbles, focusedId])

  // Set container ref for focus management
  const setContainerRef = useCallback((element: HTMLElement | null) => {
    containerRef.current = element
  }, [])

  // Focus next bubble
  const focusNext = useCallback(() => {
    if (sortedBubbles.length === 0) return

    const currentIndex = sortedBubbles.findIndex(b => b.id === focusedId)
    const nextIndex = currentIndex === -1
      ? 0
      : (currentIndex + 1) % sortedBubbles.length

    // Type guard: check if bubble exists at index
    const nextBubble = sortedBubbles[nextIndex]
    if (!nextBubble) return

    setFocusedId(nextBubble.id)
    onBubbleSelect?.(nextBubble)
  }, [sortedBubbles, focusedId, onBubbleSelect])

  // Focus previous bubble
  const focusPrevious = useCallback(() => {
    if (sortedBubbles.length === 0) return

    const currentIndex = sortedBubbles.findIndex(b => b.id === focusedId)
    const prevIndex = currentIndex === -1
      ? sortedBubbles.length - 1
      : (currentIndex - 1 + sortedBubbles.length) % sortedBubbles.length

    // Type guard: check if bubble exists at index
    const prevBubble = sortedBubbles[prevIndex]
    if (!prevBubble) return

    setFocusedId(prevBubble.id)
    onBubbleSelect?.(prevBubble)
  }, [sortedBubbles, focusedId, onBubbleSelect])

  // Focus bubble in direction (radial)
  const focusInDirection = useCallback((direction: 'closer' | 'farther') => {
    if (!focusedBubble || sortedBubbles.length === 0) return

    const currentDistance = focusedBubble.distance_from_center
    const candidates = sortedBubbles.filter(b => {
      if (direction === 'closer') {
        return b.distance_from_center < currentDistance
      } else {
        return b.distance_from_center > currentDistance
      }
    })

    if (candidates.length === 0) return

    // Find closest by distance and angle
    const target = candidates.reduce((closest, bubble) => {
      const dAngle = Math.abs(bubble.angle_degrees - focusedBubble.angle_degrees)
      const dDist = Math.abs(bubble.distance_from_center - currentDistance)
      const closestDAngle = Math.abs(closest.angle_degrees - focusedBubble.angle_degrees)
      const closestDDist = Math.abs(closest.distance_from_center - currentDistance)

      // Prefer similar angle, then closer distance
      if (dAngle < closestDAngle - 10) return bubble
      if (Math.abs(dAngle - closestDAngle) < 10 && dDist < closestDDist) return bubble
      return closest
    })

    // Type guard: ensure target exists
    if (!target) return

    setFocusedId(target.id)
    onBubbleSelect?.(target)
  }, [focusedBubble, sortedBubbles, onBubbleSelect])

  // Focus bubble by angle
  const focusByAngle = useCallback((direction: 'next' | 'previous') => {
    if (sortedBubbles.length === 0) return

    const currentIndex = sortedBubbles.findIndex(b => b.id === focusedId)
    let targetIndex: number

    if (currentIndex === -1) {
      targetIndex = direction === 'next' ? 0 : sortedBubbles.length - 1
    } else {
      targetIndex = direction === 'next'
        ? (currentIndex + 1) % sortedBubbles.length
        : (currentIndex - 1 + sortedBubbles.length) % sortedBubbles.length
    }

    // Type guard: check if bubble exists at index
    const target = sortedBubbles[targetIndex]
    if (!target) return

    setFocusedId(target.id)
    onBubbleSelect?.(target)
  }, [sortedBubbles, focusedId, onBubbleSelect])

  // Clear focus
  const clearFocus = useCallback(() => {
    setFocusedId(null)
    onBubbleSelect?.(null)
  }, [onBubbleSelect])

  // Collapse/filter dropped the focused bubble — clear so hover/SR stay in sync.
  useEffect(() => {
    if (focusedId !== null && !bubbles.some(b => b.id === focusedId)) {
      setFocusedId(null)
      onBubbleSelect?.(null)
    }
  }, [bubbles, focusedId, onBubbleSelect])

  // Handle keyboard events. Native controls (Badge-Buttons, Slider, Filter)
  // must keep Tab/Enter — the section-level listener would otherwise steal them.
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    const target = e.target
    if (target instanceof HTMLElement) {
      const tag = target.tagName
      if (
        tag === 'BUTTON' ||
        tag === 'INPUT' ||
        tag === 'SELECT' ||
        tag === 'TEXTAREA' ||
        tag === 'A' ||
        target.isContentEditable
      ) {
        return
      }
    }

    switch (e.key) {
      case 'Tab':
        e.preventDefault()
        if (e.shiftKey) {
          focusPrevious()
        } else {
          focusNext()
        }
        break

      case 'ArrowRight':
        e.preventDefault()
        focusByAngle('next')
        break

      case 'ArrowLeft':
        e.preventDefault()
        focusByAngle('previous')
        break

      case 'ArrowUp':
        e.preventDefault()
        focusInDirection('closer')
        break

      case 'ArrowDown':
        e.preventDefault()
        focusInDirection('farther')
        break

      case 'Enter':
      case ' ': e.preventDefault()
        if (focusedBubble && onBubbleActivate) {
          onBubbleActivate(focusedBubble)
        }
        break

      case 'Escape':
        e.preventDefault()
        clearFocus()
        break
    }
  }, [focusNext, focusPrevious, focusByAngle, focusInDirection, clearFocus, focusedBubble, onBubbleActivate])

  // Attach keyboard listener
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    container.addEventListener('keydown', handleKeyDown)
    return () => container.removeEventListener('keydown', handleKeyDown)
  }, [handleKeyDown])

  // Note: setContainerRef is not used in this component but is part of the API
  // It's available for parent components that need to attach the ref manually
  void setContainerRef

  return {
    focusedId,
    setFocusedId,
    focusedBubble,
    handleKeyDown,
    focusNext,
    focusPrevious,
    clearFocus,
  }
}
